<template>
  <div class="restore">
    <div class="restore__inner">
      <div class="restore__content">
        <div v-if="step===1" >
          <h3 class="restore__title">{{ $t('views.Restore.step1.title') }}</h3>
          <p class="restore__text"> {{ $t('views.Restore.step1.text') }}</p>
          <div class="restore__credentials">
            <v-input
              class="mb-1"
              :class="errorInputClass"
              :error="$t(firstStepErrors.login)"
              :placeholder="$t('components.ManagersList.newManager.login')"
              v-model="firstStep.login"
            ></v-input>
            <v-button @click="checkLogin()" @keypress.enter="checkLogin()" width="full" :loading="loading">{{ $t('views.Restore.step1.sendPass') }}</v-button>
          </div>
        </div>
        <div v-if="step===2" >
          <h3 class="restore__title">{{ $t('views.Restore.step2.title') }}</h3>
          <p class="restore__text"> {{ $t('views.Restore.step2.text') }}</p>
          <div class="restore__credentials">
            <v-input
              class="mb-1"
              :class="errorInputClass"
              type-name="number"
              :error="$t(secondStepErrors.code)"
              :placeholder="$t('components.ManagersList.newManager.pin')"
              v-model="secondStep.code"
            ></v-input>
            <v-button @click="checkCode()" @keypress.enter="checkCode()" width="full" :loading="loading">{{ $t('views.Restore.step2.sendCode') }}</v-button>
            <v-button @click="prevStep()" class="mt-1" color="grey" width="full" >{{ $t('components.AddReferral.backward') }}</v-button>
          </div>
        </div>
        <div v-if="step===3" >
          <h3 class="restore__title">{{ $t('views.Restore.step3.title') }}</h3>
          <p class="restore__text"> {{ $t('views.Restore.step3.text') }}</p>
          <div class="restore__credentials">
            <v-input
              class="mb-1"
              :class="errorInputClass"
              :error="$t(thirdStepErrors.password)"
              :placeholder="$t('components.ManagersList.newManager.password')"
              :type-name="typePassword ? 'password' : null"
              v-model="thirdStep.password"
              icon-type="button"
              icon-position="right"
              :icon="typePassword ? 'icon-eye' : 'icon-crossed-out-eye'"
              @iconClick="typePassword = !typePassword"
            ></v-input>
            <v-input
              class="mb-2"
              :class="errorInputClass"
              :error="$t(thirdStepErrors.password)"
              :placeholder="$t('components.ManagersList.newManager.secondPassword')"
              :type-name="typePassword2 ? 'password' : null"
              v-model="thirdStep.secondPassword"
              icon-type="button"
              icon-position="right"
              :icon="typePassword2 ? 'icon-eye' : 'icon-crossed-out-eye'"
              @iconClick="typePassword2 = !typePassword2"
            ></v-input>
            <v-button @click="checkPass()" width="full" @keypress.enter="checkPass()" :loading="loading">{{ $t('views.Restore.step3.savePass') }}</v-button>
            <v-button @click="prevStep()" class="mt-1" color="grey" width="full" >{{ $t('components.AddReferral.backward') }}</v-button>
          </div>
        </div>
      </div>
    </div>

    <div class="step__counter">
      <div class="step__counter-item" v-for="(item, index) in 3" :key="item" :class="{
          'step__counter-item--active': index === step - 1,
        }"></div>
    </div>
  </div>
</template>

<script>

import { notifyGraphqlError } from '@/mixins/handleGraphqlError'

import vInput from '@/ui/controls/vInput'
import vButton from '@/ui/controls/vButton'

import { RestorePassword, ResetPassPinCode, ResetPass } from '@/graphql/mutations'
import { onLogin } from '@/vue-apollo'

export default {
  components: {
    vInput,
    vButton
  },

  mixins: [notifyGraphqlError],

  data () {
    return {
      step: 1,

      firstStep: {
        login: ''
      },
      secondStep: {
        code: '',
        token: '',
        reCapcha: null
      },
      thirdStep: {
        reCapcha: null,
        password: '',
        secondPassword: ''
      },

      firstStepErrors: {
        login: null,
        reCapcha: null
      },
      secondStepErrors: {
        code: null,
        reCapcha: null
      },
      thirdStepErrors: {
        password: null,
        reCapcha: null
      },

      errorInputClass: null,
      loading: false,

      typePassword: true,
      typePassword2: true
    }
  },
  methods: {
    nextStep () {
      this.step++
    },
    prevStep () {
      if (this.step > 1) this.step--
    },

    checkLogin () {
      if (this.firstStep.login === '') {
        this.firstStepErrors.login = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        return
      }
      this.firstStepErrors.login = null
      this.sendCode()
    },
    checkCode () {
      if (this.secondStep.code === '') {
        this.secondStepErrors.code = 'errors.Components.AddOrder.validation.mustNotBeEmpty'
        return
      }
      this.secondStepErrors.code = null
      this.confirmCode()
    },
    checkPass () {
      if (this.thirdStep.password === '' || this.thirdStep.secondPassword === '') {
        this.$notify({
          type: 'error',
          message: this.$i18n.t('errors.Components.AddOrder.validation.mustNotBeEmpty'),
          delay: 150,
          timeout: 3000
        })
        return
      }
      if (this.thirdStep.password !== this.thirdStep.secondPassword) {
        this.$notify({
          type: 'error',
          message: this.$i18n.t('errors.codes.passesNotEqual'),
          delay: 150,
          timeout: 5000
        })
        return
      }
      this.thirdStepErrors.password = null
      this.changePass()
    },

    async sendCode () {
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: RestorePassword,
          variables: {
            params: {
              login: this.firstStep.login,
              domain: window.location.hostname
            }
          }
        })
        this.loading = false
        this.nextStep()
      } catch (e) {
        this.loading = false
        this.notifyGraphqlError(e)
      }
    },
    async confirmCode () {
      this.loading = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: ResetPassPinCode,
          variables: {
            params: {
              login: this.firstStep.login,
              code: this.secondStep.code
            }
          }
        })

        this.secondStep.token = data.resetPassPinCode

        this.loading = false
        this.nextStep()
        //
      } catch (e) {
        this.loading = false
        this.notifyGraphqlError(e)
      }
    },
    async changePass () {
      this.loading = true

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ResetPass,
          variables: {
            params: {
              token: this.secondStep.token,
              newPass: this.thirdStep.password
            }
          }
        })

        try {
          await onLogin(this.$apollo.getClient(), data.resetPass.accessToken, data.resetPass.refreshToken)
          await this.$router.push({
            name: 'dashboard'
          })
        } catch (e) {
          this.loading = false
          this.notifyGraphqlError(e)
        }
      } catch (e) {
        this.loading = false
        this.notifyGraphqlError(e)
      }
    }
  },
  mounted () {
    if (this.$route.query.login && this.$route.query.code) {
      this.firstStep.login = this.$route.query.login
      this.secondStep.code = this.$route.query.code
      this.step = 2
      this.confirmCode()
    }
  }
}

</script>

<style lang="scss">
.icon-close{
  opacity: 1 !important;
}
.modal__header > div{
  opacity: 1 !important;
}
.restore{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  min-width: 28rem;
  position: relative;

  @include computedVh(90);

  @include respond(phone-x-large) {
    justify-content: space-between;
  }
  &__title {
    margin-top: calc(5rem + env(safe-area-inset-top));
    margin-bottom: 1rem;
    font-weight: 200;
    font-size: 2rem;
    order: 1;

    font-style: normal;
    line-height: 130%;
    color: $color-grey;

    @include respond(phone-medium) {
      //margin-bottom: 4rem;
    }
  }
  &__text {
    font-size: 2rem;
    margin-bottom: 5rem;
  }
  &__inner {
    padding: 2rem;
    margin-left: $default-spacing-mobile;
    margin-right: $default-spacing-mobile;
    align-self: center;
    max-width: 40rem;

    //background-color: rgba($color-azure-light, .4);
    //border-radius: $border-radius-large;
    text-align: center;
    /*padding: 3rem 5rem;*/

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    @include respond(phone-medium) {
      /*padding: 5rem 5rem;*/
    }

    @include respond(phone-x-large) {
      border-radius: $border-radius-large;
    }
  }

  &__content {
    width: 100%;
  }
  &__credentials{
    max-width: 30rem;
    margin: auto;
  }
}

.step {
  &__counter {
    padding-top: 2rem;
    padding-bottom: 1rem;

    display: flex;
    justify-content: center;

    &-item {
      width: 1.2rem;
      height: 1.2rem;
      margin: .5rem;
      border-radius: 50%;
      background-color: $color-secondary;
      opacity: 0.4;
      transition: all .2s;

      &--active {
        opacity: 1;
      }
    }
  }
}
</style>
